import type { CreateOpeningsResponse } from "@edu/shared";
import { dayTitle, weekdayName } from "./common.ts";
import { openingsOutcome } from "./openings.ts";

/**
 * Части диалога создания, которые нужны только виду «Открыть для записи»
 * (`docs/booking-design.md`, 1.6 и 3.2).
 *
 * Своим файлом, потому что `CreateOccurrenceDialog.tsx` и так стоит за
 * четырьмястами строками. Состояние живёт у диалога, как у соседних полей в
 * `OccurrenceFormFields.tsx`: сюда приезжают значения, отсюда уезжают события.
 */

/** Два вида диалога. Переключатель есть только у диалога, открытого из сетки. */
export type CreateKind = "lesson" | "opening";

export const CREATE_KIND_SEGMENTS = [
  { id: "lesson", label: "Занятие" },
  { id: "opening", label: "Открыть для записи" },
] as const;

/**
 * «Повторять каждую неделю до».
 *
 * ⚠️ **Это пачка, а не серия, и подпись обязана это сказать.** Рядом, в виде
 * «Занятие», стоит «каждую неделю» у серии, и человек, видевший его там,
 * прочитает здесь то же самое: что ученик запишется на все недели сразу. На деле
 * каждая неделя это отдельное время, ученик берёт одно, а остальные остаются
 * открытыми другим (решение владельца 1 в 1.1 проекта: только разово).
 */
export function OpeningRepeatField({
  on,
  until,
  min,
  weekday,
  onToggle,
  onUntil,
}: {
  on: boolean;
  until: string;
  /** Дата первого окна: раньше неё «до» не бывает. */
  min: string;
  /** День недели по ISO, из даты первого окна. */
  weekday: number;
  onToggle: (on: boolean) => void;
  onUntil: (date: string) => void;
}) {
  return (
    <>
      <fieldset className="field">
        <legend className="lbl">Повторение</legend>
        {/* Два ответа, а не галочка, как у `RepeatChoice`: галочка «повторять»
            читалась бы свойством, которое можно не заметить */}
        <label className="form__check">
          <input type="radio" name="batch" checked={!on} onChange={() => onToggle(false)} />
          один раз
        </label>
        <label className="form__check">
          <input type="radio" name="batch" checked={on} onChange={() => onToggle(true)} />
          каждую неделю, {weekdayName(weekday)}
        </label>
        {on && (
          <label className="field">
            <span className="lbl">До какого дня</span>
            <input
              className="fld"
              type="date"
              min={min}
              value={until}
              onChange={(e) => onUntil(e.target.value)}
            />
          </label>
        )}
      </fieldset>
      {on && (
        <p className="hint">
          Время откроется отдельно на каждую неделю. Это не серия: ученик записывается на одно
          занятие, остальные недели остаются свободными.
        </p>
      )}
    </>
  );
}

/**
 * Что не открылось. Показывается в диалоге ДО закрытия, как `ScheduleSkipped`.
 *
 * Строк бывает до шестидесяти, поэтому это список, а не всплывающая полоса:
 * полоса исчезает сама, а пропуск недели в пачке это дырка в опубликованном
 * расписании, и узнать о ней преподаватель обязан.
 */
export function OpeningsOutcome({ res }: { res: CreateOpeningsResponse }) {
  const out = openingsOutcome(res, dayTitle);
  return (
    <div className="note">
      <p>{out.head}</p>
      {out.lines.length > 0 && (
        <ul className="occ__skipped">
          {out.lines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
